import { formatPaise, paiseToRupeeInput, rupeesToPaise } from "@/lib/format";
import type {
	PriceRule,
	QuantityRule,
} from "../services/boq-templates-api.service";

/** Units an author can pick. A subset of the backend `uom` enum, which also
 *  carries non-unit values (template_a, draft, ...) that make no sense here. */
export const UNITS = [
	"sq_ft",
	"sq_m",
	"running_ft",
	"meter",
	"piece",
	"nos",
	"set",
	"box",
	"bag",
	"kg",
	"liter",
	"roll",
	"lot",
	"job",
] as const;

export const QUANTITY_KINDS: { value: QuantityRule["kind"]; label: string }[] =
	[
		{ value: "fixed", label: "Fixed quantity" },
		{ value: "area_multiplier", label: "Property area × multiplier" },
		{ value: "room_count", label: "Bedrooms / bathrooms × multiplier" },
		{ value: "scope_room_count", label: "Rooms in project scope × multiplier" },
	];

/** The editable shape of a rule: every number is a string until it is saved. */
export interface RuleForm {
	quantityKind: QuantityRule["kind"];
	value: string;
	multiplier: string;
	fallbackQuantity: string;
	roomSource: "property.rooms.bedrooms" | "property.rooms.bathrooms";
	priceKind: PriceRule["kind"];
	priceValue: string;
}

export const EMPTY_RULE_FORM: RuleForm = {
	quantityKind: "fixed",
	value: "1",
	multiplier: "1",
	fallbackQuantity: "1",
	roomSource: "property.rooms.bedrooms",
	priceKind: "catalog_price",
	priceValue: "0",
};

export function ruleFormFrom(q: QuantityRule, p: PriceRule): RuleForm {
	const form: RuleForm = { ...EMPTY_RULE_FORM, quantityKind: q.kind };
	if (q.kind === "fixed") {
		form.value = String(q.value);
	} else {
		form.fallbackQuantity = String(q.fallbackQuantity);
		form.multiplier = String(q.multiplier ?? 1);
		if (q.kind === "room_count") form.roomSource = q.source;
	}
	form.priceKind = p.kind;
	if (p.kind === "fixed") form.priceValue = paiseToRupeeInput(p.value);
	return form;
}

/** `whole` for quantities: the engine rounds them to integers, so a
 *  fraction typed here would be saved as one number and generated as another. */
function num(
	raw: string,
	label: string,
	min: "positive" | "zero",
	whole = false,
): number {
	const n = Number(raw);
	if (raw.trim() === "" || !Number.isFinite(n)) {
		throw new Error(`${label} must be a number`);
	}
	if (whole && !Number.isInteger(n)) {
		throw new Error(`${label} must be a whole number`);
	}
	if (min === "positive" ? n <= 0 : n < 0) {
		throw new Error(
			`${label} must be ${min === "positive" ? "greater than zero" : "zero or more"}`,
		);
	}
	return n;
}

/** The fixed price is typed in rupees and sent as whole paise. */
function priceInPaise(raw: string): number {
	const cleaned = raw.replace(/,/g, "").trim();
	const n = Number(cleaned);
	if (cleaned === "" || !Number.isFinite(n)) {
		throw new Error("Fixed price must be a number");
	}
	if (n < 0) throw new Error("Fixed price must be zero or more");
	const paise = rupeesToPaise(cleaned);
	if (paise === undefined) {
		throw new Error(
			"Fixed price must be in rupees with at most 2 decimal places",
		);
	}
	return paise;
}

/** Throws an Error whose message is shown to the author. */
export function rulesFromForm(f: RuleForm): {
	quantityRule: QuantityRule;
	priceRule: PriceRule;
} {
	let quantityRule: QuantityRule;
	switch (f.quantityKind) {
		case "fixed":
			quantityRule = {
				kind: "fixed",
				value: num(f.value, "Quantity", "positive", true),
			};
			break;
		case "area_multiplier":
			quantityRule = {
				kind: "area_multiplier",
				source: "property.sizeSqft",
				multiplier: num(f.multiplier, "Multiplier", "positive"),
				fallbackQuantity: num(
					f.fallbackQuantity,
					"Fallback quantity",
					"zero",
					true,
				),
			};
			break;
		case "room_count":
			quantityRule = {
				kind: "room_count",
				source: f.roomSource,
				multiplier: num(f.multiplier, "Multiplier", "positive"),
				fallbackQuantity: num(
					f.fallbackQuantity,
					"Fallback quantity",
					"zero",
					true,
				),
			};
			break;
		case "scope_room_count":
			quantityRule = {
				kind: "scope_room_count",
				source: "project.scopeDetails.rooms",
				multiplier: num(f.multiplier, "Multiplier", "positive"),
				fallbackQuantity: num(
					f.fallbackQuantity,
					"Fallback quantity",
					"zero",
					true,
				),
			};
			break;
	}
	const priceRule: PriceRule =
		f.priceKind === "fixed"
			? { kind: "fixed", value: priceInPaise(f.priceValue) }
			: { kind: "catalog_price" };
	return { quantityRule, priceRule };
}

export function describeQuantity(q: QuantityRule): string {
	switch (q.kind) {
		case "fixed":
			return `${q.value} fixed`;
		case "area_multiplier":
			return `area × ${q.multiplier} (else ${q.fallbackQuantity})`;
		case "room_count":
			return `${q.source.endsWith("bedrooms") ? "bedrooms" : "bathrooms"} × ${q.multiplier ?? 1} (else ${q.fallbackQuantity})`;
		case "scope_room_count":
			return `scope rooms × ${q.multiplier ?? 1} (else ${q.fallbackQuantity})`;
	}
}

export function describePrice(p: PriceRule): string {
	return p.kind === "fixed"
		? `fixed ${formatPaise(p.value)}`
		: "catalogue price";
}

export function slugify(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 120);
}
